import React from 'react';
import { LoadingMark } from '@/components/ui/LoadingMark';
import { useLoadingPreset } from '@/hooks/shared/useOrgPreset';

interface SplashScreenProps {
  /** Estado único vindo do App (`useAppInitialization`) — nunca duplicar o hook aqui */
  progress: number;
  currentStep: string;
  isComplete: boolean;
  customMessage?: string;
}

/**
 * Splash alinhada ao protótipo Vertto (`Paginas/Loading/Loading.jsx`):
 * fundo branco, `/icone.png` + animação `animate-loading-float`, «LOADING», pontinhos,
 * mensagem de estado, barra em pill roxa #4C3DAE, percentual e «Pronto!» em verde.
 */
export const SplashScreen: React.FC<SplashScreenProps> = ({
  progress,
  currentStep,
  isComplete,
  customMessage,
}) => {
  const displayMessage = customMessage || currentStep || 'Carregando...';

  const pct = Math.round(Math.min(progress, 100));
  // Org com marca própria: fundo, barra e textos nas cores dela (ver OrgPreset.loading).
  const brand = useLoadingPreset()?.loading;

  return (
    <div
      className="flex min-h-screen w-full flex-col items-center justify-center bg-white px-6 py-8 font-sans antialiased text-gray-800"
      style={brand ? { backgroundColor: brand.background, color: brand.muted } : undefined}
    >
      <div className="flex w-full max-w-md flex-col items-center gap-8 select-none">
        <LoadingMark />

        <div className="flex w-full flex-col items-center gap-3">
          <h1
            className="text-lg font-medium uppercase tracking-widest text-gray-500 md:text-xl"
            style={brand ? { color: brand.muted } : undefined}
          >
            Loading
          </h1>
          <div className="flex space-x-2" aria-hidden>
            <span className="loading-screen-dot" style={brand ? { backgroundColor: brand.accent } : undefined} />
            <span className="loading-screen-dot" style={brand ? { backgroundColor: brand.accent } : undefined} />
            <span className="loading-screen-dot" style={brand ? { backgroundColor: brand.accent } : undefined} />
          </div>
        </div>

        <div className="flex w-full flex-col items-center gap-4">
          <p
            className="min-h-[1.25rem] text-center font-funnel text-sm text-gray-500 transition-all duration-300 ease-in-out"
            style={brand ? { color: brand.muted } : undefined}
          >
            {displayMessage}
          </p>

          <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-[#4C3DAE] transition-[width] duration-300 ease-out"
              style={{ width: `${pct}%`, ...(brand ? { backgroundColor: brand.accent } : {}) }}
            />
          </div>

          <p
            className="text-sm font-medium tabular-nums text-gray-700"
            style={brand ? { color: brand.accent } : undefined}
          >
            {pct}%
          </p>

          {isComplete && (
            <div className="flex items-center gap-2 text-green-600 animate-fade-in">
              <span className="h-2 w-2 shrink-0 rounded-full bg-green-600" aria-hidden />
              <span className="text-sm font-medium">Pronto!</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
